<script lang="ts">
  /**
   * "My scores": every personal best the player has, grouped by mode, on the
   * shell's `ListPage` drawn as a page over the column (`frame="page"`, shell
   * 1.3.0; golden `listpage-page`). Each
   * row names its exact configuration and the result, with the date it was
   * set and the global record and holder for that same configuration under
   * it, and a gold trophy before the result where the player *is* that
   * holder.
   *
   * Signed in, it lists the synced set (`account.bests`); as a guest, the
   * set on this device (`personal-bests-io.ts`). The two are never merged
   * here.
   *
   * A guest can never hold a trophy: a guest score never reaches the server,
   * so the records can never name one as a holder, and `isHolder` is false
   * for a guest rather than compared against a username that does not exist.
   *
   * The rows are built only while the page is open (the page frame is the
   * shell's `FullPage`, which renders its content only then). A guest's bests are read from
   * `localStorage`, which is not reactive, so a list built once at mount
   * would never show a best set after it.
   */
  import type { ComponentProps } from 'svelte';

  import { Group, Icon, ListPage, Row, listSection } from '@artibens/app-shell';
  import type { AnyListSection, ListPagePageProps } from '@artibens/app-shell';
  import { MODE_IDS, buildMode, defaultConfigFor, describeModeConfig } from '@interlock/modes';
  import type { ModeId } from '@interlock/modes';

  import { account } from '../state/account.svelte.js';
  import { describeRecordGap } from '../state/record-gap.js';
  import { parseModeKey } from '../state/mode-key.js';
  import { loadAllPersonalBests } from '../state/personal-bests-io.js';
  import { recordFor, records } from '../state/records.svelte.js';

  const {
    open,
    onclose,
    onPlay,
  }: {
    open: boolean;
    /** The empty state's way to a first run: closes this page and opens the New game picker. */
    onPlay: () => void;
    /** Every way of closing the page: the parent owns `open` (`docs/decisions/0047`). */
    onclose: () => void;
  } = $props();

  interface RawBest {
    readonly modeKey: string;
    readonly value: number;
    readonly betterWhen: 'lower' | 'higher';
    readonly formatted: string;
    readonly achievedAtMs: number;
  }

  // Reads `open`, so a guest's bests are read again on every visit.
  const rawBests = $derived<readonly RawBest[]>(
    !open
      ? []
      : account.status === 'signed-in'
        ? account.bests
        : Object.entries(loadAllPersonalBests()).map(([modeKey, best]) => ({
            modeKey,
            value: best.value,
            betterWhen: best.betterWhen,
            formatted: best.formatted,
            achievedAtMs: best.achievedAt,
          })),
  );

  const recordsLoaded = $derived(records.status === 'ready');
  const ownUsername = $derived(account.player?.username.toLowerCase() ?? null);

  interface ScoreRow {
    readonly modeKey: string;
    readonly configLabel: string;
    readonly formatted: string;
    readonly achievedAtMs: number;
    readonly recordText: string | null;
    readonly isHolder: boolean;
  }

  /** One mode's bests, newest first: what one section of the page draws. */
  interface ModeGroup {
    readonly id: ModeId;
    readonly name: string;
    readonly entries: readonly ScoreRow[];
  }

  const groups = $derived<readonly ModeGroup[]>(
    MODE_IDS.map((id) => ({
      id,
      name: buildMode(defaultConfigFor(id)).copy.name,
      entries: rawBests
        .flatMap((entry): ScoreRow[] => {
          const config = parseModeKey(entry.modeKey);
          if (config === null || config.modeId !== id) return [];
          const record = recordFor(entry.modeKey);
          const isHolder =
            ownUsername !== null &&
            record !== null &&
            record.username.toLowerCase() === ownUsername;
          return [
            {
              modeKey: entry.modeKey,
              configLabel: describeModeConfig(config),
              formatted: entry.formatted,
              achievedAtMs: entry.achievedAtMs,
              recordText: recordsLoaded ? describeRecordGap(entry, record, config) : null,
              isHolder,
            },
          ];
        })
        .sort((a, b) => b.achievedAtMs - a.achievedAtMs),
    })),
  );

  const hasAnyScores = $derived(groups.some((group) => group.entries.length > 0));

  /**
   * One section per mode with a best, under the mode's name, every one drawn
   * by the one `modeRows` snippet from its own group (`ListSection<T>`,
   * shell 1.4.0); the note on the last says where the list lives.
   */
  const sections = $derived.by<AnyListSection[]>(() => {
    if (!hasAnyScores) return [listSection({ id: 'empty', content: empty })];
    const filled = groups.filter((group) => group.entries.length > 0);
    const note =
      account.status === 'signed-in'
        ? 'Synced with your account.'
        : 'Stored on this device. Sign in to sync it and post it to the leaderboard.';
    return filled.map((group, index) =>
      listSection({
        id: group.id,
        label: group.name,
        content: modeRows,
        args: group,
        testid: 'my-scores-list',
        ...(index === filled.length - 1 ? { note } : {}),
      }),
    );
  });

  /** A page of its own over the column: the compiler holds it to a title and a way back. */
  const page = $derived<ComponentProps<typeof ListPage> & ListPagePageProps>({
    frame: 'page',
    open,
    title: 'My scores',
    onback: onclose,
    sections,
    testid: 'my-scores-sheet',
  });

  function formatDate(ms: number): string {
    return new Date(ms).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }
</script>

{#snippet trophy()}
  <span
    class="trophy"
    role="img"
    data-testid="record-trophy"
    title="You hold the global record"
    aria-label="Global record holder"><Icon name="trophy" size={18} /></span
  >
{/snippet}

{#snippet empty()}
  <Group>
    <!-- A row's label stays on one line (the shell's `Row`), so the sentence
         is a short label and a `below` that wraps, never cut short. -->
    <Row
      label="No personal bests yet"
      below="Finish a run and it shows up here."
      tone="muted"
      testid="my-scores-empty"
    />
    <Row label="Start a new game" tone="action" testid="my-scores-play" onclick={onPlay} />
  </Group>
{/snippet}

{#snippet modeRows(group: ModeGroup)}
  <Group>
    {#each group.entries as entry (entry.modeKey)}
      <Row
        label={entry.configLabel}
        below={entry.recordText === null
          ? formatDate(entry.achievedAtMs)
          : `${formatDate(entry.achievedAtMs)} · ${entry.recordText}`}
        value={entry.formatted}
        mark={entry.isHolder ? trophy : undefined}
        testid="my-score-row"
      />
    {/each}
  </Group>
{/snippet}

<ListPage {...page} />

<style>
  .trophy {
    display: inline-flex;
    color: var(--ui-gold);
  }
</style>
